import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './UserSatisfactionRate.module.scss';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import ReactApexChart from 'react-apexcharts';
import { useFetch } from 'hooks/useFetch';
import { dashboardEndpoints } from 'hooks/endpoints';
import Tooltip from 'component/Custom/tooltip/Tooltip';
import NoData from 'component/molecules/Table/TableComponents/NoData/NoData';

const UserSatisfactionRate = ({
  botId,
  startDate,
  endDate,
  snapshotMode,
  botType,
}) => {
  const { data: satisfactionRate, refetch: refetchSatisfication } = useFetch(
    ['satisfactionRate', startDate, endDate, botId, botType],
    dashboardEndpoints.getSatisfactionRate(startDate, endDate),
    { enabled: false },
    botId ? { botId: botId, chatSource: botType } : {}
  );

  useEffect(() => {
    if (botType !== undefined) {
      refetchSatisfication();
    }
  }, [startDate, endDate, botType, botId]);

  const satisfied = satisfactionRate?.data?.[0]?.satisfied ?? 0;
  const notSatisfied = satisfactionRate?.data?.[0]?.notSatisfied ?? 0;
  const hasData = satisfied > 0 || notSatisfied > 0;

  const series = [satisfied, notSatisfied];

  const cardRef = useRef(null);
  const headerRef = useRef(null);
  const chartWrapRef = useRef(null);

  const [chartH, setChartH] = useState(260);
  const [scale, setScale] = useState(1.2);

  useLayoutEffect(() => {
    const ro = new ResizeObserver(() => {
      const cardH = cardRef.current?.clientHeight ?? 0;
      const headerH = headerRef.current?.offsetHeight ?? 0;
      const legendH = 56;
      const padding = 16;
      const MAX_CHART_H = 280;

      const availableH = Math.max(180, cardH - headerH - legendH - padding);
      setChartH(Math.min(MAX_CHART_H, availableH));

      const wrapW = chartWrapRef.current?.clientWidth ?? 0;
      const base = Math.max(1, Math.min(wrapW || 1, availableH));
      const targetScale = (availableH * 2) / base;
      setScale(Math.max(1, Math.min(1.5, targetScale)));
    });

    if (cardRef.current) ro.observe(cardRef.current);
    if (chartWrapRef.current) ro.observe(chartWrapRef.current);
    return () => ro.disconnect();
  }, []);

  const options = useMemo(
    () => ({
      chart: {
        type: 'donut',
        id: 'user-satisfaction-rate',
        sparkline: { enabled: true },
        parentHeightOffset: 0,
        fontFamily: 'Poppins, sans-serif',
      },
      labels: ['Satisfied', 'Not Satisfied'],
      colors: ['#3A07D6', '#B2A7FF'],
      stroke: { colors: ['#FFFFFF'], width: 1, lineCap: 'butt' },
      plotOptions: {
        pie: {
          startAngle: -90,
          endAngle: 90,
          customScale: snapshotMode ? 1 : scale,
          offsetY: 24,
          donut: { size: '60%' },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(1)}%`,
        style: {
          fontSize: `${(13 / scale).toFixed(2)}px`,
          fontWeight: 700,
          colors: ['#FFFFFF'],
        },
        dropShadow: { enabled: false },
      },
      tooltip: { enabled: false },
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        offsetY: -6,
        fontSize: '14px',
        fontWeight: 500,
        markers: { radius: 12, width: 14, height: 14 },
        labels: { colors: '#101828' },
      },
      grid: { padding: { top: 0, right: 0, bottom: 0, left: 0 } },
    }),
    [scale]
  );

  return (
    <div ref={cardRef} className={styles['resolution-rate-container']}>
      <div ref={headerRef} className={styles['title-container']}>
        <span
          className={`${styles['title']} ${
            snapshotMode && styles['snapshotMode-title']
          }`}
        >
          User Satisfaction Rate
        </span>
        {!snapshotMode && (
          <Tooltip
            className={styles['tooltip']}
            content='Shows how satisfied users are based
on ratings collected over time.'
          >
            <SvgWithBranding type='info' color='#682ECB' />
          </Tooltip>
        )}
      </div>

      <div ref={chartWrapRef} className={styles['chart']}>
        {hasData ? (
          <ReactApexChart
            options={options}
            series={series}
            type='donut'
            height={chartH}
          />
        ) : (
          <NoData noDataIcon='noUser' minHeight={snapshotMode ? 400 : chartH} />
        )}
      </div>
    </div>
  );
};

export default UserSatisfactionRate;
